import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _regeneratorRuntime from "@babel/runtime/regenerator";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { taskYield } from './backgroundTasks';
export function createEmptyHeatmapEntry() {
  return {
    head: null,
    previousEntries: []
  };
}

/**
 * Represents a rectangular area in the heatmap.
 */

export function cloneEntry(entry) {
  if (!entry.head) {
    return {
      head: null,
      previousEntries: _toConsumableArray(entry.previousEntries)
    };
  }
  return {
    head: {
      time: entry.head.time,
      elementName: entry.head.elementName,
      wrapperSectionName: entry.head.wrapperSectionName,
      rect: entry.head.rect,
      source: entry.head.source,
      ratio: entry.head.ratio
    },
    previousEntries: _toConsumableArray(entry.previousEntries)
  };
}

/**
 * Clones a 2D array of numbers.
 *
 * This function creates a deep copy of the provided 2D array, which is faster than using `structuredClone`.
 *
 * @param {ReadonlyArray<ReadonlyArray<HeatmapEntry>>} arr - The 2D array to clone.
 * @returns {Array<Array<number>>} A deep copy of the original 2D array.
 */
export function cloneArray(arr) {
  // This is much faster than `structuredClone(arr)`
  return arr.map(function (a) {
    return a.map(cloneEntry);
  });
}
export function isRectInside(a, b) {
  if (!a || !b) {
    return false;
  }

  // Check if all corners of rectangle a are within the bounds of rectangle b
  return a.left >= b.left && a.right <= b.right && a.top >= b.top && a.bottom <= b.bottom;
}

/**
 * Parameters required for transforming a heatmap.
 */

/**
 * Transforms the heatmap by updating the rectangles with the current start time.
 *
 * Iterates over the specified rectangle area within the heatmap and updates each cell with the provided start time.
 * If a row is undefined, the provided callback is invoked.
 *
 * Attention! This function does mutate the heatmap
 *
 * @param {TransformHeatmapProps} props - The properties for transforming the heatmap.
 * @returns {Heatmap | undefined} The updated heatmap or undefined if a row is not found.
 */
export function transformHeatmap(_ref) {
  var rect = _ref.rect,
    heatmap = _ref.heatmap,
    transformSource = _ref.transformSource,
    onEmptyRow = _ref.onEmptyRow,
    entry = _ref.entry;
  var _roundedBottom$rounde = {
      roundedBottom: Math.min(Math.trunc(rect.bottom), heatmap.height),
      roundedTop: Math.trunc(rect.top),
      roundedLeft: Math.trunc(rect.left),
      roundedRight: Math.min(Math.trunc(rect.right), heatmap.width)
    },
    roundedBottom = _roundedBottom$rounde.roundedBottom,
    roundedTop = _roundedBottom$rounde.roundedTop,
    roundedLeft = _roundedBottom$rounde.roundedLeft,
    roundedRight = _roundedBottom$rounde.roundedRight;
  var localHeatmap = heatmap.map;
  for (var row = roundedTop; row < roundedBottom; row++) {
    for (var col = roundedLeft; col < roundedRight; col++) {
      if (localHeatmap[row] === undefined || localHeatmap[row][col] === undefined) {
        onEmptyRow({
          entry: entry,
          row: row
        });
      } else {
        var previousEntry = localHeatmap[row][col].head;

        // When elements are added at the same time
        // we try to keep the inner element changes as the head
        // This logic is really important to make sure placeholders are properly ignored
        if ((previousEntry === null || previousEntry === void 0 ? void 0 : previousEntry.time) === entry.time && isRectInside(previousEntry.rect, entry.rect)) {
          localHeatmap[row][col].previousEntries.push(_objectSpread(_objectSpread({}, entry), {}, {
            source: 'mutation:parent-mounted'
          }));
          continue;
        }

        // Check for the same dimensions and elementName
        var isSameDimensions = isRectInside(entry.rect, previousEntry === null || previousEntry === void 0 ? void 0 : previousEntry.rect) && isRectInside(previousEntry === null || previousEntry === void 0 ? void 0 : previousEntry.rect, entry.rect);
        var isSameElementName = entry.elementName === (previousEntry === null || previousEntry === void 0 ? void 0 : previousEntry.elementName);
        if (transformSource === 'mutation' && isSameDimensions && isSameElementName) {
          localHeatmap[row][col].head = _objectSpread(_objectSpread({}, entry), {}, {
            source: 'mutation:node-replacement'
          });
        } else {
          localHeatmap[row][col].head = _objectSpread(_objectSpread({}, entry), {}, {
            source: transformSource || null
          });
        }
        if (previousEntry !== null) {
          localHeatmap[row][col].previousEntries.push(previousEntry);
        }
      }
    }
  }
}
function createDOMRect(x, y, width, height) {
  if (typeof DOMRect !== 'undefined') {
    // DOMRect well supported supported
    // eslint-disable-next-line compat/compat
    return new DOMRect(x, y, width, height);
  } else {
    // Fallback for environments without DOMRect
    return {
      x: x,
      y: y,
      width: width,
      height: height,
      get left() {
        return this.x;
      },
      get right() {
        return this.x + this.width;
      },
      get top() {
        return this.y;
      },
      get bottom() {
        return this.y + this.height;
      },
      toJSON: function toJSON() {}
    };
  }
}

/**
 * Maps a DOMRect to a heatmap's coordinate system.
 *
 * Converts the dimensions of a DOMRect to the heatmap's coordinate system using the heatmap's scaling factors.
 *
 * @param {object} props - The properties required for mapping.
 * @param {DOMRect} props.rect - The DOMRect to map.
 * @param {Heatmap} props.heatmap - The heatmap with scaling information.
 * @returns {HeatmapRect} The mapped heatmap rectangle.
 */
export function mapDOMRectToHeatmap(props) {
  var rect = props.rect,
    _props$heatmap = props.heatmap,
    scaleX = _props$heatmap.scaleX,
    scaleY = _props$heatmap.scaleY;
  var scaledX = rect.x * scaleX;
  var scaledY = rect.y * scaleY;
  var scaledWidth = rect.width * scaleX;
  var scaledHeight = rect.height * scaleY;
  var n = createDOMRect(scaledX, scaledY, scaledWidth, scaledHeight);
  return {
    left: n.left,
    right: n.right,
    top: n.top,
    bottom: n.bottom
  };
}

/**
 * Calculates the ratio of a heatmap rectangle compared to the full heatmap.
 *
 * This function determines what fraction of the heatmap is covered by the given heatmap rectangle.
 *
 * @param {object} props - The properties required for ratio calculation.
 * @param {HeatmapRect} props.heatmapRect - The heatmap rectangle.
 * @param {Heatmap} props.heatmap - The full heatmap dimensions.
 * @returns {number} The ratio of the rectangle area to the heatmap area.
 */
export function getElementRatio(props) {
  var _props$rect = props.rect,
    right = _props$rect.right,
    left = _props$rect.left,
    bottom = _props$rect.bottom,
    top = _props$rect.top;
  if (props.viewport.w === 0 || props.viewport.h === 0) {
    return 0;
  }
  var ratio = (right - left) * (bottom - top) / (props.viewport.w * props.viewport.h);
  if (ratio > 1) {
    return 1;
  }
  return parseFloat(ratio.toFixed(5));
}
/**
 * Initializes a square heatmap with all values set to zero.
 *
 * Creates a 2D array with the specified size, where each value is initialized to zero.
 *
 * @param {number} heatmapSize - The size of the heatmap (both width and height).
 * @returns {HeatmapEntry[][]} A 2D array representing the heatmap, filled with empty HeatmapEntry.
 */
export function cleanSquareHeatmap(heatmapSize) {
  return Array.from({
    length: heatmapSize
  }).map(function () {
    return Array.from({
      length: heatmapSize
    }).map(createEmptyHeatmapEntry);
  });
}
/**
 * Creates a new heatmap array based on the aspect ratio of the viewport.
 *
 * This function adjusts the heatmap dimensions to match the aspect ratio of the viewport,
 * ensuring that the heatmap scales proportionally to the viewport size. The maximum allowable
 * heatmap size is capped at 1000 to prevent excessive memory usage.
 *
 * @param {ViewportDimension} viewport - The dimensions of the viewport.
 * @param {number} heatmapSize - The base size of the heatmap, capped at a maximum of 1000.
 * @returns {Heatmap} A new heatmap object with calculated dimensions and scaling factors.
 */
export function createHeatmapWithAspectRatio(_ref2) {
  var viewport = _ref2.viewport,
    heatmapSize = _ref2.heatmapSize;
  var maxHeatmapSize = 1000;
  var safeSize = Math.min(heatmapSize, maxHeatmapSize);
  if (viewport.w === 0 || viewport.h === 0) {
    return {
      width: safeSize,
      height: safeSize,
      scaleX: 1,
      scaleY: 1,
      map: cleanSquareHeatmap(safeSize)
    };
  }
  var aspectRatio = viewport.w / viewport.h;

  // Calculate heatmap dimensions based on aspect ratio
  var heatmapWidth;
  var heatmapHeight;
  if (aspectRatio > 1) {
    // Landscape orientation
    heatmapWidth = safeSize;
    heatmapHeight = Math.round(safeSize / aspectRatio);
  } else {
    // Portrait orientation
    heatmapHeight = safeSize;
    heatmapWidth = Math.round(safeSize * aspectRatio);
  }

  // Initialize the heatmap with zeros
  var map = Array.from({
    length: heatmapHeight
  }).map(function () {
    return Array.from({
      length: heatmapWidth
    }).map(createEmptyHeatmapEntry);
  });
  var scaleX = heatmapWidth / viewport.w;
  var scaleY = heatmapHeight / viewport.h;
  return {
    width: heatmapWidth,
    height: heatmapHeight,
    scaleX: scaleX,
    scaleY: scaleY,
    map: map
  };
}
export function createHeatmapFromEvents(_x, _x2) {
  return _createHeatmapFromEvents.apply(this, arguments);
}
function _createHeatmapFromEvents() {
  _createHeatmapFromEvents = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(events, initialHeatmap) {
    var nextHeatmap, i, _events$i, type, startTime, data, elementName, wrapperSectionName, source, heatmapRect, ratio, entry;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) switch (_context.prev = _context.next) {
        case 0:
          nextHeatmap = _objectSpread(_objectSpread({}, initialHeatmap), {}, {
            map: cloneArray(initialHeatmap.map)
          });
          i = 0;
        case 2:
          if (!(i < events.length)) {
            _context.next = 17;
            break;
          }
          _events$i = events[i], type = _events$i.type, startTime = _events$i.startTime, data = _events$i.data;
          if (!(type !== 'element:changed')) {
            _context.next = 6;
            break;
          }
          return _context.abrupt("continue", 14);
        case 6:
          elementName = data.elementName, wrapperSectionName = data.wrapperSectionName, source = data.source;
          heatmapRect = mapDOMRectToHeatmap({
            rect: data.rect,
            heatmap: nextHeatmap
          });
          ratio = getElementRatio({
            rect: heatmapRect,
            viewport: {
              w: nextHeatmap.width,
              h: nextHeatmap.height
            }
          });
          entry = {
            time: startTime,
            elementName: elementName,
            wrapperSectionName: wrapperSectionName,
            ratio: ratio,
            rect: heatmapRect
          };
          transformHeatmap({
            rect: heatmapRect,
            entry: entry,
            heatmap: nextHeatmap,
            transformSource: source,
            onEmptyRow: function onEmptyRow(_ref3) {
              var entry = _ref3.entry,
                row = _ref3.row;
            }
          });

          // Every 10 events processed
          // we give the browser the power
          // to process any other high priority task
          if (!(i % 10 === 0)) {
            _context.next = 14;
            break;
          }
          _context.next = 14;
          return taskYield();
        case 14:
          i++;
          _context.next = 2;
          break;
        case 17:
          return _context.abrupt("return", nextHeatmap);
        case 18:
        case "end":
          return _context.stop();
      }
    }, _callee);
  }));
  return _createHeatmapFromEvents.apply(this, arguments);
}
export function createHeatmapFromTimeline(timeline, initialHeatmap) {
  var events = timeline.getEvents();
  return createHeatmapFromEvents(events, initialHeatmap);
}
export function isLayoutShift(entry) {
  if (!entry || typeof entry.source !== 'string') {
    return false;
  }
  return entry.source === 'layout-shift';
}
export function isNodeReplacement(entry) {
  if (!entry || typeof entry.source !== 'string') {
    return false;
  }
  return entry.source === 'mutation:node-replacement';
}
export function isElementMoved(entry) {
  if (!entry || typeof entry.source !== 'string') {
    return false;
  }
  return entry.source === 'layout-shift:element-moved';
}